// ---------------------------------------------------------------------------
// ServicesList Renderer (editor key: ServicesList)
// ---------------------------------------------------------------------------
// Renders a grid of service cards (e.g. for a photographer, agency, or
// professional services site). Editor input is a pipe-delimited textarea
// ("Title | Description | Price | URL" per line). Price is treated as
// arbitrary display text and escaped — currency math belongs in commerce
// blocks. URLs route through the safelist; missing URLs render the card
// without a CTA link. Column count is clamped 1-4.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface ServicesListProps {
  heading?: string;
  services?: string;
  columns?: number | string;
  ctaLabel?: string;
}

interface ServiceEntry {
  title: string;
  description: string;
  price: string;
  href: string;
}

function parseServices(value: unknown): ServiceEntry[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [titlePart, descPart, pricePart, hrefPart] = line.split("|");
      return {
        title: (titlePart ?? "").trim(),
        description: (descPart ?? "").trim(),
        price: (pricePart ?? "").trim(),
        href: (hrefPart ?? "").trim(),
      };
    })
    .filter((entry) => entry.title.length > 0);
}

function clampColumns(value: unknown): number {
  const parsed =
    typeof value === "number" ? value : Number.parseInt(String(value ?? ""), 10);
  if (Number.isNaN(parsed)) return 3;
  return Math.max(1, Math.min(4, Math.floor(parsed)));
}

function renderCard(entry: ServiceEntry, ctaLabel: string): string {
  const priceHtml = entry.price
    ? `\n      <p class="ease-services-list__price">${text(entry.price)}</p>`
    : "";
  const descHtml = entry.description
    ? `\n      <p class="ease-services-list__description">${text(entry.description)}</p>`
    : "";
  const linkHtml = entry.href
    ? `\n      <a class="ease-services-list__link" href="${url(entry.href)}">${text(ctaLabel)}</a>`
    : "";

  return `    <article class="ease-services-list__item">
      <h3 class="ease-services-list__title">${text(entry.title)}</h3>${descHtml}${priceHtml}${linkHtml}
    </article>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as ServicesListProps;

  const services = parseServices(p.services);
  if (services.length === 0) {
    return "<!-- services-list missing services -->";
  }

  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const columns = clampColumns(p.columns);
  const ctaLabel =
    typeof p.ctaLabel === "string" && p.ctaLabel.trim()
      ? p.ctaLabel.trim()
      : "Learn more";

  const headingHtml = heading
    ? `\n  <h2 class="ease-services-list__heading">${text(heading)}</h2>`
    : "";

  const cards = services.map((entry) => renderCard(entry, ctaLabel)).join("\n");

  return `<section class="ease-section ease-services-list ease-services-list--cols-${columns}" data-ease-columns="${attr(String(columns))}">${headingHtml}
  <div class="ease-services-list__grid" role="list">
${cards}
  </div>
</section>`;
}

registerRenderer("services-list", { render });
